import React, { useEffect, useState } from 'react'

import { Box, Text, theme } from '@factorialco/deprecated-design-system'
import { F0Button, UpsellRequestResponseDialog } from '@factorialco/f0-react'
import { ChevronLeft, ChevronRight, Cross } from '@factorialco/f0-react/icons/app'
import { InAppCommsModal } from 'generated/resources/datatable.graphql'

import i18n from 'modules/core/lib/i18n'

import StepMedia from 'modules/in_app_comms/components/AnnouncementModal/StepAnnouncementModal/StepMedia'
import CommsButton from 'modules/in_app_comms/components/CommsButton'

interface StepAnnouncementModalProps {
  content: InAppCommsModal
  componentName: string
  onDismiss: () => void
  handleClose: () => void
  handleUpsellSuccess: () => void
  handleUpsellError: () => void
  handleUpsellClose: () => void
  upsellSuccess: boolean
  upsellError: boolean
}

const TRANSITION_DURATION = 200

const StepAnnouncementModal = ({
  content,
  componentName,
  onDismiss,
  handleClose,
  handleUpsellSuccess,
  handleUpsellError,
  handleUpsellClose,
  upsellSuccess,
  upsellError: _upsellError,
}: StepAnnouncementModalProps) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [displayedStep, setDisplayedStep] = useState(0)
  const [isVisible, setIsVisible] = useState(true)

  // Combine main content as first step with the rest of the steps
  const allSteps = [
    {
      title: content.title,
      description: content.description,
      media: content.media,
    },
    ...content.steps,
  ]
  const totalSteps = allSteps.length

  const currentStepData = allSteps[displayedStep]

  useEffect(() => {
    if (currentStep !== displayedStep) {
      setIsVisible(false)

      const timer = setTimeout(() => {
        setDisplayedStep(currentStep)
        setIsVisible(true)
      }, TRANSITION_DURATION)

      return () => clearTimeout(timer)
    }
  }, [currentStep, displayedStep])

  if (!currentStepData) return null

  const goToStep = (index: number) => {
    if (index >= 0 && index < totalSteps) {
      setCurrentStep(index)
    }
  }

  const goToPrevious = () => goToStep(currentStep - 1)
  const goToNext = () => goToStep(currentStep + 1)

  return (
    <>
      <Box
        background="grey000White"
        borderRadius={{ all: 'abs016' }}
        width="s600"
        position="relative"
        style={{
          overflowX: 'hidden',
          overflowY: 'auto',
          maxHeight: '90vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          position="absolute"
          style={{
            top: '8px',
            right: '8px',
            zIndex: theme.zIndex.modals + 2,
          }}
        >
          <F0Button
            onClick={handleClose}
            variant="outline"
            size="md"
            icon={Cross}
            hideLabel
            round
            label="Close"
          />
        </Box>

        <Box
          style={{
            opacity: isVisible ? '1' : '0',
            transform: isVisible ? 'translateX(0)' : 'translateX(8px)',
            transition: `opacity ${TRANSITION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), transform ${TRANSITION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          }}
        >
          <StepMedia media={currentStepData.media} />

          <Box padding="s16" paddingBottom="s8" gap="s8">
            <Box
              gap="s8"
              alignItems="flexStart"
              style={{ maxWidth: '90%', textAlign: 'flexStart' }}
            >
              <Text.Header2 weight="semibold" ellipsis lineClamp={2}>
                {currentStepData.title}
              </Text.Header2>
              <Text.Normal size="300" ellipsis lineClamp={3}>
                {currentStepData.description}
              </Text.Normal>
            </Box>
          </Box>
        </Box>

        <Box padding="s16" paddingTop="s8" paddingBottom="s24">
          <Box flexDirection="row" gap="s8" justifyContent="spaceBetween">
            <Box flexDirection="row" gap="s8">
              {content.buttons.map((button, index) => (
                <CommsButton
                  key={index}
                  button={button}
                  size="md"
                  variant={
                    content.buttons.length === 1 ? 'default' : index === 0 ? 'outline' : 'default'
                  }
                  componentType="in_app_comms_modal"
                  componentName={componentName}
                  onUpsellSuccess={handleUpsellSuccess}
                  onUpsellError={handleUpsellError}
                  onClick={() => {
                    if (button.type !== 'CommsUpsellButtonRecord') {
                      onDismiss()
                    }
                  }}
                  upsellRequestComponentType="in_app_comms_modal"
                />
              ))}
            </Box>
            <Box flexDirection="row" gap="s8" alignItems="center">
              <Box style={{ width: '24px', height: '24px' }}>
                <F0Button
                  onClick={goToPrevious}
                  variant="outline"
                  size="sm"
                  icon={ChevronLeft}
                  hideLabel
                  round
                  label="Previous"
                  disabled={currentStep === 0}
                />
              </Box>

              <Box flexDirection="row" gap="s4" alignItems="center">
                {allSteps.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => goToStep(index)}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '4px',
                      backgroundColor:
                        index === currentStep ? theme.colors.grey1000 : theme.colors.grey300,
                      cursor: 'pointer',
                      transition: 'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                ))}
              </Box>

              <Box style={{ width: '24px', height: '24px' }}>
                <F0Button
                  onClick={goToNext}
                  variant="outline"
                  size="sm"
                  icon={ChevronRight}
                  hideLabel
                  round
                  label="Next"
                  disabled={currentStep === totalSteps - 1}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <UpsellRequestResponseDialog
        open={upsellSuccess}
        success={upsellSuccess}
        portalContainer={document.getElementById('root')}
        errorMessage={{
          title: i18n.t('upselling_kit.upselling_button.error_message.title'),
          description: i18n.t('upselling_kit.upselling_button.error_message.description'),
        }}
        successMessage={{
          title: i18n.t('upselling_kit.upselling_button.success_message.title_2'),
          description: i18n.t('upselling_kit.upselling_button.success_message.description'),
        }}
        closeLabel={i18n.t('upselling_kit.upselling_button.close_label')}
        onClose={handleUpsellClose}
      />
    </>
  )
}

export default StepAnnouncementModal
